const NAMED_ENTITIES: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
    ndash: '–',
    mdash: '—',
    hellip: '…',
    lsquo: '‘',
    rsquo: '’',
    ldquo: '“',
    rdquo: '”',
};

/**
 * Decode HTML entities (named + numeric) in a plain-text string. SSR-safe — no
 * DOM — so it runs the same in the loader and the browser.
 */
const decodeEntities = (text: string): string =>
    text.replace(/&(#x?[0-9a-f]+|[a-z][a-z0-9]*);/gi, (match, code: string) => {
        if (code[0] === '#') {
            const isHex = code[1] === 'x' || code[1] === 'X';
            const codePoint = parseInt(
                isHex ? code.slice(2) : code.slice(1),
                isHex ? 16 : 10,
            );
            return Number.isNaN(codePoint)
                ? match
                : String.fromCodePoint(codePoint);
        }
        return NAMED_ENTITIES[code.toLowerCase()] ?? match;
    });

/** Strips HTML tags from a CMS rich-text value down to a single plain-text line. */
export const stripHtml = (html: string): string =>
    decodeEntities(html.replace(/<[^>]*>/g, ' '))
        .replace(/\s+/g, ' ')
        .trim();

/**
 * The first complete sentence of a plain-text value — for intros that must
 * never cut mid-sentence (a fixed character or line clamp would). A boundary
 * is `.`, `!` or `?` (plus an optional closing quote) followed by the end of
 * the text or whitespace leading into a capital/opening quote — lowercase
 * continuations ("e.g. steel") and figures ("No. 1", "est. 1995") don't end
 * the sentence. Boundaries inside the first few words (initials like
 * "J. Smith") are skipped via a minimum length. Falls back to the full text
 * when no boundary is found.
 */
export const firstSentence = (text: string): string => {
    const trimmed = text.trim();
    const MIN_SENTENCE_CHARS = 20;
    const boundary = /[.!?]["'”’]?(?=\s+["'“‘]?[A-Z]|\s*$)/g;
    let match: RegExpExecArray | null;
    while ((match = boundary.exec(trimmed)) !== null) {
        const end = match.index + match[0].length;
        if (end >= MIN_SENTENCE_CHARS) return trimmed.slice(0, end);
    }
    return trimmed;
};
